import { useUser } from 'hooks/useUser';
import { t } from 'i18next';
import { EventOccasion } from 'models/EventOccasion';
import { LocalTrack } from 'models/LocalTrack';

export enum PlaylistExportType {
  M3U = 'm3u',
  CRATE = 'crate',
  CSV = 'csv',
}

export function usePlaylistExport(event: EventOccasion) {
  const { preferences } = useUser();

  const createPlaylistM3u = (playlist: LocalTrack[]) => {
    const playlistM3u = playlist
      .map((track) => `#EXTINF:${0},${track?.artist} - ${track?.title}\n${track?.path}`)
      .join('\n');

    if (playlistM3u) {
      return playlistM3u;
      // createDownloadFile(playlistM3u, `${event.name}.m3u`);
    }

    return null;
  };

  const createAndSaveCrate = async (playlist: LocalTrack[]) => {
    const crateName = event.name;
    const driveLetterTrackPathMap: Record<string, string[]> = {};
    const driveLetterCrateBufferMap: Record<string, Buffer> = {};

    // Sort by drive letter
    for (const track of playlist) {
      const driveLetter = track.path[0].toUpperCase();

      if (!driveLetterTrackPathMap[driveLetter]) {
        driveLetterTrackPathMap[driveLetter] = [];
      }

      driveLetterTrackPathMap[driveLetter].push(track.path);
    }

    // Create a crate buffer for each drive letter
    for (const driveLetter in driveLetterTrackPathMap) {
      const trackPaths = driveLetterTrackPathMap[driveLetter];
      const crateBuffer = window.electron?.createCrateBuffer?.(crateName, trackPaths);

      if (crateBuffer) {
        if (!driveLetterCrateBufferMap[driveLetter]) {
          driveLetterCrateBufferMap[driveLetter] = crateBuffer;
        }
      }
    }

    // Save crate files
    for (const driveLetter in driveLetterCrateBufferMap) {
      const crateBuffer = driveLetterCrateBufferMap[driveLetter];
      await window.electron?.saveCrate(driveLetter, crateName, crateBuffer);
    }
  };

  const createPlaylistCsv = (playlist: LocalTrack[]) => {
    if (!playlist?.length) return;

    const headers = [
      t('title', 'Title'),
      t('artist', 'Artist'),
      t('album', 'Album'),
      t('duration', 'Duration'),
      t('path', 'Path'),
    ];

    const rows = playlist.map((track) => [
      track.title ?? '',
      track.artist ?? '',
      track.collection ?? '',
      track.duration ?? '',
      track.path ?? '',
    ]);

    const escapeValue = (value: string | number) => {
      const str = String(value);
      if (/[;"\n]/.test(str)) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map(escapeValue).join(';')),
    ].join('\n');

    const BOM = '\uFEFF';

    return BOM + csvContent;
    // createDownloadFile(BOM + csvContent, `${event.name}.csv`);
  };

  const createPlaylistByType = (type: PlaylistExportType, playlist: LocalTrack[]) => {
    switch (type) {
      case PlaylistExportType.M3U:
        return createPlaylistM3u(playlist);
      case PlaylistExportType.CRATE:
        return createAndSaveCrate(playlist);
      case PlaylistExportType.CSV:
        return createPlaylistCsv(playlist);
    }
  };

  const createPlaylistDefault = (playlist: LocalTrack[]) => {
    return createPlaylistByType(getDefaultPlaylistType(), playlist);
  };

  const getDefaultPlaylistType = () => {
    return preferences.defaultExportType;
  };

  return {
    createPlaylistM3u,
    createAndSaveCrate,
    createPlaylistCsv,
    createPlaylistByType,
    createPlaylistDefault,
    getDefaultPlaylistType,
  };
}
